import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  ManageNewsCategoryPayload,
  NewsCategory,
  NewsCategoryImage,
} from '../../../../core/models/cityparking/news.model';
import { ApiNewsService } from '../../../../core/services/api/cityparking/api-news.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-news-categories',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, PssConfirmModalComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './news-categories.component.html',
  styleUrl: './news-categories.component.css',
})
export class NewsCategoriesComponent implements OnInit {
  private readonly api = inject(ApiNewsService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  items = signal<NewsCategory[]>([]);
  isLoading = signal(false);
  isSaving = signal(false);
  isUploading = signal(false);
  lastFetchedAt = signal<Date | null>(null);

  searchText = '';
  filterActive: '' | 'true' | 'false' = '';

  isModalOpen = signal(false);
  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  form: ManageNewsCategoryPayload = this.emptyForm();
  modalImages = signal<NewsCategoryImage[]>([]);

  isConfirmDeactivateOpen = false;
  pendingDeactivateImage: NewsCategoryImage | null = null;
  private brokenImageIds = new Set<number>();

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  get isEdit(): boolean {
    return (this.form.id ?? 0) > 0;
  }

  get filteredItems(): NewsCategory[] {
    const q = this.searchText.trim().toLowerCase();
    return this.items().filter((item) => {
      if (this.filterActive === 'true' && !item.active) return false;
      if (this.filterActive === 'false' && item.active) return false;
      if (!q) return true;
      return (
        item.name.toLowerCase().includes(q) ||
        item.code.toLowerCase().includes(q) ||
        (item.nameEn || '').toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q)
      );
    });
  }

  ngOnInit(): void {
    this.load();
  }

  onRefresh(): void {
    this.load(this.isModalOpen() && this.isEdit ? this.form.id : undefined);
  }

  onSearch(): void {
    // client-side filter via getter
  }

  openCreate(): void {
    this.form = this.emptyForm();
    this.modalImages.set([]);
    this.brokenImageIds.clear();
    this.isModalOpen.set(true);
  }

  openEdit(item: NewsCategory): void {
    this.form = {
      userId: 0,
      menuId: 0,
      id: item.id,
      code: item.code,
      name: item.name,
      nameEn: item.nameEn ?? '',
      description: item.description ?? '',
      sortOrder: item.sortOrder ?? 0,
      active: item.active,
    };
    this.modalImages.set([...(item.images ?? [])]);
    this.brokenImageIds.clear();
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    if (this.isSaving() || this.isUploading()) return;
    this.isModalOpen.set(false);
    this.form = this.emptyForm();
    this.modalImages.set([]);
  }

  isImageBroken(imageId: number): boolean {
    return this.brokenImageIds.has(imageId);
  }

  onImageError(imageId: number): void {
    this.brokenImageIds.add(imageId);
  }

  askDeactivateImage(img: NewsCategoryImage): void {
    this.pendingDeactivateImage = img;
    this.isConfirmDeactivateOpen = true;
  }

  closeConfirmDeactivate(): void {
    this.isConfirmDeactivateOpen = false;
    this.pendingDeactivateImage = null;
  }

  confirmDeactivateImage(): void {
    const img = this.pendingDeactivateImage;
    this.closeConfirmDeactivate();
    if (!img) return;
    this.onDeactivateImage(img);
  }

  onSave(): void {
    if (!this.form.code?.trim() || !this.form.name?.trim()) {
      this.toast.error('กรุณาระบุ code และชื่อ');
      return;
    }

    if (this.isEdit && !this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!this.isEdit && !this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const auth = this.resolveAuth();
    if (!auth) return;

    this.isSaving.set(true);
    this.api
      .manageNewsCategory({
        ...this.form,
        userId: auth.userId,
        menuId: auth.menuId,
        code: this.form.code.trim().toLowerCase(),
        name: this.form.name.trim(),
      })
      .subscribe({
        next: (res) => {
          this.isSaving.set(false);
          if (!res?.isSuccess) {
            this.toast.error(res?.message || 'บันทึกไม่สำเร็จ');
            return;
          }
          this.toast.success(res.message || 'บันทึกสำเร็จ');
          const keepId = Number(res.IdReturn || this.form.id || 0);
          this.load(keepId > 0 ? keepId : undefined, true);
        },
        error: (err) => {
          this.isSaving.set(false);
          this.toast.error(err?.error?.message || 'บันทึกไม่สำเร็จ');
        },
      });
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const categoryId = this.form.id;
    if (!categoryId || categoryId <= 0) {
      this.toast.error('บันทึกประเภทก่อน แล้วค่อยอัปโหลดรูป');
      input.value = '';
      return;
    }
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์อัปโหลด');
      input.value = '';
      return;
    }

    const auth = this.resolveAuth();
    if (!auth) {
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.isUploading.set(true);
      this.api
        .uploadNewsCategoryImage({
          userId: auth.userId,
          menuId: auth.menuId,
          categoryId,
          fileName: file.name,
          base64: String(reader.result || ''),
        })
        .subscribe({
          next: (res) => {
            this.isUploading.set(false);
            input.value = '';
            if (!res?.isSuccess) {
              this.toast.error(res?.message || 'อัปโหลดไม่สำเร็จ');
              return;
            }
            this.toast.success('อัปโหลดรูปสำเร็จ');
            this.load(categoryId, true);
          },
          error: (err) => {
            this.isUploading.set(false);
            input.value = '';
            this.toast.error(err?.error?.message || 'อัปโหลดไม่สำเร็จ');
          },
        });
    };
    reader.readAsDataURL(file);
  }

  onDeactivateImage(img: NewsCategoryImage): void {
    if (!this.canDelete) {
      this.toast.error('ไม่มีสิทธิ์ลบ');
      return;
    }
    const auth = this.resolveAuth();
    if (!auth) return;

    this.api
      .deactivateNewsCategoryImage({
        userId: auth.userId,
        menuId: auth.menuId,
        id: img.id,
      })
      .subscribe({
        next: (res) => {
          if (!res?.isSuccess) {
            this.toast.error(res?.message || 'ไม่สำเร็จ');
            return;
          }
          this.toast.success('ปิดใช้งานแล้ว');
          this.load(this.form.id || undefined, true);
        },
        error: (err) => this.toast.error(err?.error?.message || 'ไม่สำเร็จ'),
      });
  }

  private load(selectId?: number, keepModalOpen = false): void {
    this.isLoading.set(true);
    this.api.getNewsCategories(false).subscribe({
      next: (list) => {
        this.brokenImageIds.clear();
        this.items.set(list ?? []);
        this.lastFetchedAt.set(new Date());
        this.isLoading.set(false);

        if (selectId && selectId > 0) {
          const found = (list ?? []).find((x) => x.id === selectId);
          if (found && (keepModalOpen || this.isModalOpen())) {
            this.form = {
              userId: 0,
              menuId: 0,
              id: found.id,
              code: found.code,
              name: found.name,
              nameEn: found.nameEn ?? '',
              description: found.description ?? '',
              sortOrder: found.sortOrder ?? 0,
              active: found.active,
            };
            this.modalImages.set([...(found.images ?? [])]);
            this.isModalOpen.set(true);
          }
        }
      },
      error: (err) => {
        this.isLoading.set(false);
        this.toast.error(err?.error?.message || 'โหลดไม่สำเร็จ');
      },
    });
  }

  private emptyForm(): ManageNewsCategoryPayload {
    return {
      userId: 0,
      menuId: 0,
      id: 0,
      code: '',
      name: '',
      nameEn: '',
      description: '',
      sortOrder: 0,
      active: true,
    };
  }

  private resolveAuth(): { userId: number; menuId: number } | null {
    const local = localStorage.getItem('userId');
    const userId = local ? Number(local) : Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู — ตั้ง path_id 77 ในเมนู DB');
      return null;
    }
    return { userId, menuId };
  }

  get canShowActivityHistory(): boolean {
    return (this.form.id ?? 0) > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
